import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { RecordDetail, RecordVersion } from '../api/types';
import { RecordDiff } from '../components/RecordDiff';

export function RecordView() {
  const { objectType, recordId } = useParams<{ objectType: string; recordId: string }>();
  const navigate = useNavigate();
  const [record, setRecord] = useState<RecordDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null);

  useEffect(() => {
    if (!objectType || !recordId) return;
    let cancelled = false;

    async function fetchRecord() {
      try {
        const data = await api.get<RecordDetail>(`/records/${objectType}/${recordId}`);
        if (!cancelled) {
          setRecord(data);
          setError(null);
          if (data.versions.length > 0) {
            setSelectedVersion(data.versions[0].versionId);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load record');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    fetchRecord();
    return () => { cancelled = true; };
  }, [objectType, recordId]);

  function addToCart() {
    if (!record) return;
    // Get existing cart from sessionStorage
    const existing = sessionStorage.getItem('restore_cart');
    const cart = existing ? JSON.parse(existing) : [];
    const alreadyInCart = cart.some((item: { recordId: string }) => item.recordId === record.recordId);
    if (!alreadyInCart) {
      cart.push({
        objectType: record.objectType,
        recordId: record.recordId,
        name: record.name,
        versionId: selectedVersion,
      });
      sessionStorage.setItem('restore_cart', JSON.stringify(cart));
    }
    navigate('/restore/plan');
  }

  function getSelectedVersionData(): RecordVersion | undefined {
    return record?.versions.find((v) => v.versionId === selectedVersion);
  }

  function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  }

  if (isLoading) {
    return (
      <div role="status" aria-live="polite">
        <p>Loading record…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div role="alert">
        <div className="card" style={{ borderLeft: '4px solid var(--color-error)' }}>
          <p style={{ color: 'var(--color-error)' }}>{error}</p>
        </div>
      </div>
    );
  }

  if (!record) return null;

  const currentVersion = getSelectedVersionData();

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
        <div>
          <h2 style={{ marginBottom: '0.25rem' }}>{record.name}</h2>
          <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>
            <span className="badge badge-info">{record.objectType}</span>
            <code style={{ marginLeft: '0.5rem', fontSize: '0.8rem' }}>{record.recordId}</code>
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {record.isCurrentlyDeleted && (
            <span className="badge badge-error" style={{ alignSelf: 'center' }}>DELETED</span>
          )}
          <button onClick={addToCart} aria-label="Add this record to restore cart">
            Add to Restore Cart
          </button>
        </div>
      </div>

      {/* Version Timeline */}
      <section aria-labelledby="versions-heading">
        <h3 id="versions-heading">Version History ({record.versions.length} version{record.versions.length !== 1 ? 's' : ''})</h3>
        <div className="card">
          <div style={{ display: 'flex', gap: '1rem', overflowX: 'auto', paddingBottom: '0.5rem' }}>
            {record.versions.map((version) => (
              <button
                key={version.versionId}
                className={version.versionId === selectedVersion ? undefined : 'secondary'}
                onClick={() => setSelectedVersion(version.versionId)}
                aria-pressed={version.versionId === selectedVersion}
                style={{
                  whiteSpace: 'nowrap',
                  padding: '0.4rem 0.75rem',
                  fontSize: '0.75rem',
                }}
              >
                {new Date(version.timestamp).toLocaleDateString()}{' '}
                {new Date(version.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                {version.isDeleted && ' 🗑️'}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Selected Version Details */}
      {currentVersion && (
        <section aria-labelledby="version-detail-heading" style={{ marginTop: '1.5rem' }}>
          <h3 id="version-detail-heading">
            Version from {new Date(currentVersion.timestamp).toLocaleString()}
            {currentVersion.isDeleted && (
              <span className="badge badge-error" style={{ marginLeft: '0.5rem', verticalAlign: 'middle' }}>
                RECORD DELETED
              </span>
            )}
          </h3>

          {/* Field Changes */}
          <div className="card">
            <RecordDiff changes={currentVersion.changes} title="Field Changes" />
          </div>

          {/* All Fields */}
          <details style={{ marginTop: '1rem' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 500, padding: '0.5rem 0' }}>
              All Fields ({Object.keys(currentVersion.fields).length})
            </summary>
            <div className="card" style={{ marginTop: '0.5rem', maxHeight: '400px', overflow: 'auto' }}>
              <table aria-label="Record fields">
                <thead>
                  <tr>
                    <th scope="col">Field</th>
                    <th scope="col">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(currentVersion.fields).map(([key, value]) => (
                    <tr key={key}>
                      <td style={{ fontWeight: 500 }}>{key}</td>
                      <td>
                        <code style={{ fontSize: '0.8rem', wordBreak: 'break-all' }}>
                          {value === null ? 'null' : String(value)}
                        </code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </section>
      )}

      {/* Related Records */}
      {record.relatedRecords.length > 0 && (
        <section aria-labelledby="related-heading" style={{ marginTop: '1.5rem' }}>
          <h3 id="related-heading">Related Records ({record.relatedRecords.length})</h3>
          <table aria-label="Related records">
            <thead>
              <tr>
                <th scope="col">Relationship</th>
                <th scope="col">Object Type</th>
                <th scope="col">Name</th>
                <th scope="col">Record ID</th>
              </tr>
            </thead>
            <tbody>
              {record.relatedRecords.map((rel) => (
                <tr
                  key={rel.recordId}
                  onClick={() => navigate(`/records/${rel.objectType}/${rel.recordId}`)}
                  style={{ cursor: 'pointer' }}
                  tabIndex={0}
                  role="link"
                  aria-label={`View related record ${rel.name}`}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/records/${rel.objectType}/${rel.recordId}`);
                    }
                  }}
                >
                  <td>{rel.relationship}</td>
                  <td><span className="badge badge-info">{rel.objectType}</span></td>
                  <td>{rel.name}</td>
                  <td><code style={{ fontSize: '0.8rem' }}>{rel.recordId}</code></td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* ContentVersion Files */}
      {record.contentVersionFiles.length > 0 && (
        <section aria-labelledby="files-heading" style={{ marginTop: '1.5rem' }}>
          <h3 id="files-heading">Files ({record.contentVersionFiles.length})</h3>
          <table aria-label="Attached files">
            <thead>
              <tr>
                <th scope="col">Title</th>
                <th scope="col">Extension</th>
                <th scope="col">Size</th>
                <th scope="col">Version</th>
                <th scope="col">Download</th>
              </tr>
            </thead>
            <tbody>
              {record.contentVersionFiles.map((file) => (
                <tr key={file.contentDocumentId}>
                  <td>{file.title}</td>
                  <td><code>{file.fileExtension}</code></td>
                  <td>{formatFileSize(file.contentSize)}</td>
                  <td>{file.versionNumber}</td>
                  <td>
                    <a
                      href={file.downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Download ${file.title}`}
                    >
                      Download
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
